import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputAdornment,
  InputLabel,
  Link,
  Typography
} from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'

import UserAPI from '../../api/orbitV1/UserAPI'
import profilePlaceholder from '../../assets/placeholders/profile_placeholder.png' // Profile picture placeholder
import PageLayout from '../../common/Layout/Page/PageLayout'
import { ColorBoxContent, PictureWrapper } from '../../common/ui/colorBox'
import {
  ERR_ACCESS_DATA,
  ERR_UPDATE_USER,
  ERR_USER_DETAILS
} from '../../constants/errors'
import OrbitSession from '../../hooks/useCookies'

import UploadDialog from './components/UploadDialog'

/**
 * Manage the profile of the user, including changing of personal details
 * @returns {React.ReactElement}
 */
export default function Profile() {
  /* Formik variables */
  const validationSchema = yup.object({
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
  })

  const formik = useFormik({
    initialValues: {
      contactNumber: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })
  const orgId = OrbitSession.get('orgId')
  const firstLogin = OrbitSession.get('firstLogin')

  const [userId, setUserId] = useState('')
  const [name, setName] = useState('')
  const [profilePic, setProfilePic] = useState(profilePlaceholder)
  const [email, setEmail] = useState('')
  const [updatedProfilePic, setUpdatedProfilePic] = useState(null)
  const [isUpdatePicDialogOpen, setIsUpdatePicDialogOpen] = useState(false)
  const [role, setUserRole] = useState('')

  /* TODO: Send file to backend/s3 then extract to display on frontend */
  const handleUpdatePic = async () => {
    const formData = new FormData()
    // TODO: Change profile image name
    formData.append('profileImg', updatedProfilePic)

    try {
      const pfpUploaded = await UserAPI.uploadPfp(userId, formData, {})
      if (pfpUploaded) {
        // TODO: refetch profile img url
        OrbitSession.setStoreType('localstorage')
        OrbitSession.set({ profileImg: pfpUploaded.data.data })
        OrbitSession.setStoreType('cookie')
        setIsUpdatePicDialogOpen(false)
        location.href = ''
        toast.success('Profile picture updated!')
      }
    } catch (error) {
      toast.error(
        'There is an issue uploading your profile picture. Please ensure it is of jpeg, png or jpg format.'
      )
    }
  }

  const handleSaveChanges = async () => {
    const contactNumber = formik.values.contactNumber

    try {
      await UserAPI.updateUser({
        userId,
        name,
        email,
        contactNumber,
        orgId,
        role,
        firstLogin
      })

      toast.success('User information updated')
    } catch (error) {
      toast.error(ERR_UPDATE_USER)
    }
  }

  useEffect(() => {
    getProfile()
    OrbitSession.setStoreType('localstorage')
    const profileImgUrl = OrbitSession.get('profileImg')
    OrbitSession.setStoreType('cookie')
    profileImgUrl
      ? setProfilePic('data:image/png;base64,' + profileImgUrl)
      : setProfilePic(profilePlaceholder)
    setEmail(OrbitSession.get('email'))
  }, [])

  const getProfile = async () => {
    try {
      const userDetails = await UserAPI.getUserById(OrbitSession.get('_id'))
      if (userDetails && userDetails.data && userDetails.data.length > 0) {
        setName(userDetails.data[0].name)
        formik.setFieldValue('contactNumber', userDetails.data[0].contactNumber)
        setUserId(userDetails.data[0]._id)
        setUserRole(userDetails.data[0].role)
      } else {
        toast.error(ERR_ACCESS_DATA)
      }
    } catch (error) {
      toast.error(ERR_USER_DETAILS)
    }
  }

  return (
    <PageLayout header='Edit Profile'>
      <ColorBoxContent>
        <Grid container spacing={2} padding={1}>
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box>
                <PictureWrapper
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '100px'
                  }}
                  image={profilePic}
                />
              </Box>
              <Box sx={{ textAlign: 'center', marginTop: '1em' }}>
                <Link onClick={() => setIsUpdatePicDialogOpen(true)}>
                  Upload Image
                </Link>
                <Typography
                  variant='subtitle1'
                  sx={{
                    color: '#B8C4D3',
                    fontStyle: 'italic'
                  }}
                >
                  png / jpg, max 2mb
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography sx={{ marginLeft: 0 }} variant='h3'>
              <strong>{name}</strong>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <FormControl variant='standard' sx={{ marginY: '2em' }}>
                <InputLabel htmlFor='contact-number' data-cy='contactNumber'>
                  Contact Number
                </InputLabel>
                <Input
                  id='contactNumber'
                  name='contactNumber'
                  variant='standard'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>+65</InputAdornment>
                    )
                  }}
                  value={formik.values.contactNumber}
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  error={
                    formik.touched.contactNumber &&
                    Boolean(formik.errors.contactNumber)
                  }
                />
                <FormHelperText id='helper-text'>
                  {formik.touched.contactNumber && formik.errors.contactNumber}
                </FormHelperText>
              </FormControl>

              <FormControl variant='standard' sx={{ marginY: '1em' }}>
                <InputLabel htmlFor='email'>Email</InputLabel>
                <Input
                  value={email}
                  disabled
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant='filled'
                onClick={handleSaveChanges}
                data-cy='saveChanges'
              >
                Save Changes
              </Button>
              <Button
                as={RouterLink}
                to='/'
                data-cy='cancelProfileUpdate'
                variant='secondary'
                sx={{
                  backgroundColor: 'transparent',
                  textDecoration: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </ColorBoxContent>
      <UploadDialog
        isUpdatePicDialogOpen={isUpdatePicDialogOpen}
        setIsUpdatePicDialogOpen={setIsUpdatePicDialogOpen}
        setUpdatedProfilePic={setUpdatedProfilePic}
        handleUpdatePic={handleUpdatePic}
      />
    </PageLayout>
  )
}
